import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import {
  Box,
  MediaOverlay,
  Button,
  Loader,
  IconElement,
} from '@wix/design-system';
import { WidgetPlugin } from '../../../types/widgetPlugins';
import { PluginInfo } from './pluginInfo';

import { AddBold } from '@wix/wix-ui-icons-common/classic-editor';
import { DeleteSmall, ReplaceSmall } from '@wix/wix-ui-icons-common';
import { useWidgetPluginManager } from '../hooks';
import { useTranslation } from '../../useTranslation';
import { usePluginsUpdateMode } from '../hooks/usePluginsUpdateMode';
import { usePluginsBi } from '../hooks/useBi';

interface PluginCardProps {
  plugin: WidgetPlugin;
}

export const PluginCard: FC<PluginCardProps> = ({ plugin }) => {
  const bi = usePluginsBi();
  const {
    installOrReplacePlugin,
    uninstallPlugin,
    highlightSlot,
    unhighlightSlot,
  } = useWidgetPluginManager();

  const [isLoading, setIsLoading] = useState(false);

  const handlePluginInstall = useCallback(async () => {
    setIsLoading(true);
    await installOrReplacePlugin(plugin);
    setIsLoading(false);
  }, [installOrReplacePlugin, plugin]);

  const handlePluginUninstall = useCallback(async () => {
    setIsLoading(true);
    await uninstallPlugin(plugin.slot!, {
      biOrigin: 'plugin_market_card',
      controlMode: 'user',
    });
    setIsLoading(false);
  }, [uninstallPlugin, plugin]);

  const handleMouseEnter = () => {
    bi.pluginHover({
      origin: 'plugin_market_card',
      action_context: plugin.appDefinitionId,
    });
    highlightSlot(plugin.slot);
  };

  const handleMouseLeave = () => {
    unhighlightSlot(plugin.slot);
  };

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <MediaOverlay
        media={<PluginInfo plugin={plugin} />}
        hoverSkin="dark"
        skin={isLoading ? 'dark' : 'none'}
        borderRadius={6}
      >
        <MediaOverlay.Content visible={isLoading ? 'always' : 'hover'}>
          <Box
            direction="vertical"
            align="center"
            dataHook={`widgetPlugins.pluginId:${plugin.id}.overlay`}
          >
            <PluginCardAction
              plugin={plugin}
              isLoading={isLoading}
              handleInstall={handlePluginInstall}
              handleUninstall={handlePluginUninstall}
            />
          </Box>
        </MediaOverlay.Content>
      </MediaOverlay>
    </div>
  );
};

interface PluginCardActionProps {
  plugin: WidgetPlugin;
  isLoading: boolean;
  handleInstall: () => void;
  handleUninstall: () => void;
}

export const PluginCardAction: FC<PluginCardActionProps> = ({
  plugin,
  isLoading,
  handleInstall,
  handleUninstall,
}) => {
  const { isSlotPopulatedInstalled, isPluginInstalled } =
    useWidgetPluginManager();
  const [t] = useTranslation();
  const { isUpdateInProgress } = usePluginsUpdateMode();

  return useMemo(() => {
    const getButtonSettings = (): [
      IconElement | undefined,
      ReactNode,
      () => void,
      string | undefined,
      boolean,
    ] => {
      if (isLoading) {
        return [undefined, <Loader size="tiny" />, () => {}, undefined, false];
      }

      if (isPluginInstalled(plugin)) {
        return [
          <DeleteSmall />,
          t('PLATFORM_Widget_Slots_CTA_On_Hover4'),
          handleUninstall,
          'delete',
          isUpdateInProgress,
        ];
      }

      if (isSlotPopulatedInstalled(plugin)) {
        return [
          <ReplaceSmall />,
          t('PLATFORM_Widget_Slots_CTA_On_Hover5'),
          handleInstall,
          'replace',
          isUpdateInProgress,
        ];
      }

      return [
        <AddBold />,
        t('PLATFORM_Widget_Slots_CTA_On_Hover1'),
        handleInstall,
        'install',
        isUpdateInProgress,
      ];
    };

    const [buttonIcon, buttonText, handler, dataHookAction, disabled] =
      getButtonSettings();

    return (
      <Button
        onClick={handler}
        skin="light"
        size="small"
        prefixIcon={buttonIcon}
        dataHook={`widgetPlugins.action.${dataHookAction}`}
        disabled={disabled}
      >
        {buttonText}
      </Button>
    );
  }, [
    isLoading,
    handleInstall,
    handleUninstall,
    isPluginInstalled,
    isSlotPopulatedInstalled,
    plugin,
    t,
    isUpdateInProgress,
  ]);
};
